import { observer } from 'mobx-react'
import React, { JSX, useEffect, useRef, useState } from 'react'
import { toJS } from 'mobx'
import { useQueryClient } from '@tanstack/react-query'
import SearchPanel from '@Components/Search/SearchPanel'
import useSearchQuery from '@Components/Search/SearchPanel/useSearchQuery'
import useFilterUrlSync from '@Components/Search/SearchPanel/useFilterUrlSync'
import useSearchCriteriaUrlSync from '@Components/Search/SearchPanel/useSearchCriteriaUrlSync'
import CatalogExportButton from '@Scenes/RightsholderScene/RightsholderCatalog/RightsholderCatalogSearch/components/CatalogExportButton'
import MemberWorkIdentifierTooltip from '@Scenes/RightsholderScene/RightsholderCatalog/RightsholderCatalogSearch/components/MemberWorkIdentifierTooltip'
import WorkResultsCardTable from '@Components/WorkItemV2/WorkResultsCardTable'
import ModalPortal from '@Components/Popups/ModalPortal'
import Sidebar from '@Components/Sidebars/Sidebar'
import DownloadSidebar from '@Components/Sidebars/DownloadSidebar'
import { PaginationBody } from '@Entities/pagination'
import CatalogSearchStore, { defaultCatalogPagination } from '@App/stores/search/work/catalog-search-store'
import { FilterFieldGroup } from '@App/forms/common/filter-field-group'
import { Maybe } from '@Types'
import { RightsholderClaimedShareList } from '@Entities/rightsholder'
import { CardWithHeader } from '@Components/Tables/CardResults/card-with-header'
import WorkItemData from '@Components/WorkItemV2/work-item-data'
import { WorkSearch } from '@App/stores/search/models/work-search'
import { Works } from '@Entities/work/works'
import UserStore from '@App/stores/user/user-store'
import { BulkActionStore } from '@App/stores/search/bulk-action-store'
import { useBulkActionNavigation } from '@Components/BulkAction/hooks/useBulkActionNavigation'
import BulkActionLeavePageModal from '@Components/BulkAction/BulkActionLeavePageModal'

export const CATALOG_SEARCH_QUERY_KEY = 'catalog-search'

interface BulkActionConfig {
    store: BulkActionStore<string, string>
    labels: Record<string, string>
    controlSection: JSX.Element
    selectionLimit?: number
}

interface CatalogSearchProps<K> {
    userStore: UserStore
    catalogSearchStore: CatalogSearchStore
    searchFilters?: Array<FilterFieldGroup<K>>
    claimedShares?: Maybe<RightsholderClaimedShareList>
    compactWorkComponentType: CardWithHeader<WorkItemData>
    extendedWorkComponentType: React.ComponentType<WorkItemData>
    disableAutoComplete?: boolean
    bulkAction?: BulkActionConfig
    enableSort?: boolean
}

const CatalogSearch = <K extends Record<string, unknown>>({
    userStore,
    catalogSearchStore,
    searchFilters,
    claimedShares,
    compactWorkComponentType,
    extendedWorkComponentType,
    disableAutoComplete = false,
    bulkAction,
    enableSort = false,
}: CatalogSearchProps<K>) => {
    const [sidebarIsOpen, setSidebarIsOpen] = useState(false)
    const queryClient = useQueryClient()

    const { blocker, bulkActionLabel } = useBulkActionNavigation(
        bulkAction?.store,
        catalogSearchStore,
        bulkAction?.labels ?? {},
        bulkAction?.selectionLimit
    )

    // catalogSearchStore is a session-persistent singleton, so a filter left over from an earlier
    // visit must not linger into a fresh one: reset to plain defaults unless the URL says otherwise.
    // (No isEqual/dirty-check needed here, unlike similar resets elsewhere — TanStack Query hashes
    // the queryKey by content, so setting an unchanged filter doesn't cause an extra fetch.)
    const { urlFilterValues, syncFiltersToUrl } = useFilterUrlSync<K>(searchFilters)
    // A link can also carry search terms (?combinedTitles=…). They must be in the store before the form
    // first renders — it picks its rows up then — so this runs in a state initializer, not an effect.
    const { urlCriteria, syncCriteriaToUrl } = useSearchCriteriaUrlSync(
        catalogSearchStore.searchCriteriaStore.criteriaOptions
    )
    useState(() => {
        if (urlCriteria) {
            catalogSearchStore.seedSearchFromUrl(urlCriteria, urlFilterValues as Partial<K> | undefined)
        }
    })
    const hasInitializedFilterRef = useRef(false)
    useEffect(() => {
        if (!hasInitializedFilterRef.current) {
            hasInitializedFilterRef.current = true
            catalogSearchStore.setCurrentSearchFilters(urlFilterValues ?? {})
        }
    }, [])

    const pagination = toJS(catalogSearchStore.pagination)
    const searchQuery = toJS(catalogSearchStore.searchQuery)

    const worksQuery = useSearchQuery<Works>({
        queryKey: [CATALOG_SEARCH_QUERY_KEY, ...catalogSearchStore.searchScopeKey, searchQuery, pagination],
        queryFn: async () => catalogSearchStore.fetchWorks({ ...pagination }, searchQuery),
        // Copied into the store from the query's data, not from the fetch: a cache hit never runs the fetch.
        applyData: (response) => catalogSearchStore.applySearchResults(response),
        initialData: catalogSearchStore.worksRightsHolder,
        onError: (error) => catalogSearchStore.reportSearchError(error),
    })

    const closeSidebar = () => {
        setSidebarIsOpen(false)
    }

    /* Re-runs the search for the given page. Changing pagination changes the query key, and
       invalidating makes sure an already cached page is refreshed as well. */
    const runSearch = async (newPagination: PaginationBody = defaultCatalogPagination) => {
        catalogSearchStore.setPagination(newPagination)
        await queryClient.invalidateQueries({ queryKey: [CATALOG_SEARCH_QUERY_KEY] })
    }

    const reset = async () => {
        catalogSearchStore.resetSearchCriteriaStore()
        catalogSearchStore.destroyCatalogCriteria()
        catalogSearchStore.destroyCurrentSearchFilter()
        await runSearch()
    }

    const runSearchWithNewCriteria = (searchCriteria: WorkSearch, filters: Partial<K>) => {
        syncCriteriaToUrl(searchCriteria)
        catalogSearchStore.setCatalogSelectionSearchQuery(searchCriteria)
        catalogSearchStore.setCurrentSearchFilters(filters)
        catalogSearchStore.setPagination(defaultCatalogPagination)
    }

    const onChangePage = (newPagination: PaginationBody) => {
        catalogSearchStore.setPagination(newPagination)
    }

    const onChangeLimit = (newLimit: number) => {
        catalogSearchStore.applyLimitChange(newLimit)
    }

    return (
        <div className="rightsholder-catalog-wrapper">
            {bulkAction && (
                <BulkActionLeavePageModal
                    store={bulkAction.store}
                    runSearch={runSearch}
                    blocker={blocker}
                    bulkActionLabel={bulkActionLabel}
                />
            )}
            <section className="showing-search">
                <SearchPanel
                    searchCriteriaStore={catalogSearchStore.searchCriteriaStore}
                    canAddCriteria
                    onSearch={runSearchWithNewCriteria}
                    searchQueryKey={[CATALOG_SEARCH_QUERY_KEY]}
                    disableAutoComplete={disableAutoComplete}
                    filterFieldCollections={searchFilters}
                    defaultFilterValues={urlFilterValues}
                    onFilterValueChange={syncFiltersToUrl}
                    onReset={() => {
                        syncFiltersToUrl(undefined)
                        syncCriteriaToUrl(undefined)
                        reset()
                    }}
                    extraButtons={[
                        <CatalogExportButton
                            key="export-button"
                            catalogSearchStore={catalogSearchStore}
                            loading={worksQuery.isFetching}
                            setSidebarIsOpen={setSidebarIsOpen}
                        />,
                    ]}
                    isUniqueTooltip={<MemberWorkIdentifierTooltip />}
                />
            </section>
            <WorkResultsCardTable
                works={catalogSearchStore.worksRightsHolder}
                claimedShares={claimedShares}
                paginationInfo={catalogSearchStore.worksRightsHolderPaginationInfo}
                limit={catalogSearchStore.limit}
                page={catalogSearchStore.pagination.page}
                onChangePage={onChangePage}
                onChangeLimit={onChangeLimit}
                compactWorkComponentType={compactWorkComponentType}
                extendedWorkComponentType={extendedWorkComponentType}
                compact={(userStore.userInfo?.settings && userStore.userInfo.settings.showDetailedWorkCards) || false}
                showCompactToggle
                loading={worksQuery.isFetching}
                bulkActionControlSection={bulkAction?.controlSection}
            />
            <ModalPortal isOpen={sidebarIsOpen} onClose={closeSidebar}>
                <Sidebar onClose={closeSidebar}>
                    <DownloadSidebar
                        closeSidebar={closeSidebar}
                        sidebarDownloadType={catalogSearchStore.sidebarDownloadType}
                    />
                </Sidebar>
            </ModalPortal>
        </div>
    )
}

export default observer(CatalogSearch)
